import { EUiStates } from "@constants/enum";
import { showNotification } from "@easydev/Notification";
import { FileSize } from "@enums/fileSize";
import { apiUpdateModuleMap } from "@helpers/apiUpdateModule.helper";
import { getFilesForUpload } from "@helpers/filesForUpload";
import {
  getSignatureModule,
  signatureModulesMap,
} from "@helpers/signatureModule.helper";
import { IFileForUpload } from "@interfaces/Shared/file";
import { IFormValues } from "@interfaces/Shared/formValues";
import { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { TApiResult } from "types/Shared/apiResult";
import { THookCreate, THookUpdate } from "types/Shared/hooks";
import useFileUpload from "./useFileUpload";

interface IUseCreateItem {
  isModalOpen: boolean;
  isCreating: boolean;
  handleCreate: (APICreate: THookCreate) => void;
  handleIsModalOpen: () => void;
  handleSaveForm: (formValues: any, mapper: any) => void;
  totalFiles: number;
  totalSize: number;
  uploadedFiles: number;
  uploadedSize: number;
  isProgressOpen: boolean;
}

const useCreateItem = (): IUseCreateItem => {
  const { pathname } = useLocation();
  const history = useHistory();
  const [isCreating, setIsCreating] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formValues, setFormValues] = useState<IFormValues>({});
  const [filesForUpload, setFilesForUpload] = useState<IFileForUpload[]>([]);
  const [isGalleryFilesUpload, setIsGalleryFilesUpload] = useState(false);
  const [isProgressOpen, setIsProgressOpen] = useState(false);

  // TODO: check how to differientiate size limit for images and videos
  const {
    getFilesUrls,
    totalFiles,
    totalSize,
    uploadedFiles,
    uploadedSize,
    handleResetFileUpload,
  } = useFileUpload(FileSize.Max1000Mb);

  const signatureModule = getSignatureModule(pathname);

  const handleIsModalOpen = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleSaveForm = (formValues: any, mapper: any) => {
    if (formValues.gallery && typeof formValues.gallery !== "string") {
      setIsGalleryFilesUpload(true);
    }

    handleIsModalOpen();
    const formattedValues = mapper(formValues);

    setFormValues(formattedValues);
    setFilesForUpload(getFilesForUpload(formValues));
  };

  const handleCreate = async (APICreate: THookCreate) => {
    setIsCreating(true);
    handleIsModalOpen();

    APICreate(formValues)
      .then(async (result: TApiResult) => {
        // If there are no files for upload, finish create
        if (filesForUpload.length === 0) {
          handleSuccessfulCreate(result);

          return;
        }

        setIsProgressOpen(true);

        // TODO: apiUpdateModuleMap was created to select correct APIUpdate function depending on module used
        // check if there is a better way to do this

        // If there are files for upload, upload them and get their urls
        const formValuesWithUrls = await getFilesUrls(
          result.data.uuid,
          signatureModulesMap[signatureModule],
          filesForUpload,
          formValues,
          isGalleryFilesUpload
        );

        if (!formValuesWithUrls) {
          return;
        }

        // Update form values with file's urls, depending on the module
        handleUpdateFormValuesWithUrls(
          result.data.uuid,
          apiUpdateModuleMap[
            signatureModule as keyof typeof apiUpdateModuleMap
          ],
          formValuesWithUrls
        );
      })
      .catch((error) => {
        showNotification(
          "Error",
          `An error occurred while creating. | ${error}`,
          EUiStates.DANGER
        );
      })
      .finally(() => {
        setIsCreating(false);
        handleIsModalOpen();
        setIsProgressOpen(false);
        handleResetFileUpload();
      });
  };

  const handleUpdateFormValuesWithUrls = async (
    uuid: string,
    APIUpdate: THookUpdate,
    formValuesWithUrls: IFormValues
  ) => {
    APIUpdate(uuid, formValuesWithUrls).then(async (result: TApiResult) => {
      handleSuccessfulCreate(result);
    });
  };

  const handleSuccessfulCreate = (result: TApiResult) => {
    showNotification("Success", "Successfully created.", EUiStates.SUCCESS);

    history.push(
      `${pathname.replace("/create", "")}/${result.data.uuid}/detail`
    );
  };

  return {
    isModalOpen,
    isCreating,
    handleCreate,
    handleIsModalOpen,
    handleSaveForm,
    totalFiles,
    totalSize,
    uploadedFiles,
    uploadedSize,
    isProgressOpen,
  };
};

export default useCreateItem;
